import { Box } from "@mui/material";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

interface TimePickerWithSmallLabelProps {
  label?: string;
  value: string;
  onChange: (event: any) => void;
  selectedDate?: Date | string | null;
  [key: string]: any;
}

const TimePickerWithSmallLabel = ({
  label,
  value,
  onChange,
  selectedDate,
  ...props
}: TimePickerWithSmallLabelProps) => {
  const customLabelStyle = {
    ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
    color: nawyTheme.nawy.colors.semantic.text.secondary,
  };

  // Get current time in total minutes for accurate comparison
  const now = new Date();
  const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

  // Check if selected date is today
  const isToday = selectedDate
    ? (() => {
        const selected =
          typeof selectedDate === "string"
            ? new Date(selectedDate)
            : selectedDate;
        const today = new Date();
        return (
          selected.getDate() === today.getDate() &&
          selected.getMonth() === today.getMonth() &&
          selected.getFullYear() === today.getFullYear()
        );
      })()
    : true; // Default to true if no date selected to disable past times

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hours = Math.floor(i / 2);
    const minutes = i % 2 ? 30 : 0;
    const labelHours = hours % 12 === 0 ? 12 : hours % 12;
    const labelSuffix = hours < 12 ? "AM" : "PM";
    const label = `${labelHours}:${minutes
      .toString()
      .padStart(2, "0")} ${labelSuffix}`;
    const value = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;

    // Calculate total minutes for this time slot
    const timeSlotTotalMinutes = hours * 60 + minutes;

    // If selected date is today, disable time slots that have already passed
    const isDisabled = isToday && timeSlotTotalMinutes < currentTotalMinutes;

    return { label, value, disabled: isDisabled };
  });

  const selectedOption =
    timeOptions.find((option) => option.value === value) || null;

  const handleChange = (selectedOption: any) => {
    // Create a synthetic event to match the expected interface
    const syntheticEvent = {
      target: {
        value: selectedOption?.value || "",
      },
    };
    onChange(syntheticEvent);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {label && <CustomLabel sx={customLabelStyle}>{label}</CustomLabel>}
      <CustomAutoComplete
        options={timeOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder="Set time"
        fullWidth
        {...props}
      />
    </Box>
  );
};

export default TimePickerWithSmallLabel;
